import { isEnum } from 'src/utils';
import {
	IRawCommandMessageOut,
	IRawEventMessageIn,
	IRawMediaMessageIn,
	IRawMediaMessageOut,
	IRawMessageEcho,
	IRawMessageIn,
	IRawMessageOut,
	IRawText,
	IRawVoiceMessageIn,
	IRawVoiceMessageOut,
	MessageType,
} from '../../types';

/**
 * check if message is Ack/Echo because contains ONLY client_id and timestamp
 * @param message any RawMessageOut from WS
 * @returns boolean is RawMessageEcho
 */
export const checkRawMessageEcho = (
	message: Partial<IRawMessageOut>,
): message is IRawMessageEcho => {
	const { client_id, timestamp, ...rest } = message;
	return !!client_id && !!timestamp && !Object.keys(rest).length;
};

export const checkRawMessageOut = (
	message: Partial<IRawMessageOut>,
): message is IRawMessageOut => {
	return (
		!!message.timestamp && !!message.sender && !!getRawMessageType(message)
	);
};

export const getRawMessageType = (message: Partial<IRawMessageOut>) => {
	const { timestamp, sender, client_id, ...data } = message;
	const keysFound = Object.keys(data).reduce<MessageType[]>((prev, curr) => {
		if (isEnum(curr, MessageType)) return [...prev, curr];
		return prev;
	}, []);
	if (keysFound.length > 1 || !keysFound.length) return null;
	else return keysFound[0];
};

export const checkRawMessage = <
	M extends Partial<IRawMessageOut | IRawMessageIn>,
>(
	message: M,
): message is M & (IRawMessageOut | IRawMessageIn) => {
	return !!getRawMessageType(message);
};

export const isRawTextMessage = <T extends IRawMessageOut | IRawMessageIn>(
	message: T,
): message is T & IRawText => {
	return getRawMessageType(message) === MessageType.TEXT;
};
export const isRawMediaMessage = <T extends IRawMessageOut | IRawMessageIn>(
	message: T,
): message is T &
	(T extends IRawMessageOut ? IRawMediaMessageOut : IRawMediaMessageIn) => {
	return getRawMessageType(message) === MessageType.MEDIA;
};
export const isRawVoiceMessage = <T extends IRawMessageOut | IRawMessageIn>(
	message: T,
): message is T &
	(T extends IRawMessageOut ? IRawVoiceMessageOut : IRawVoiceMessageIn) => {
	return getRawMessageType(message) === MessageType.VOICE;
};
export const isRawCommandMessage = (
	message: IRawMessageOut,
): message is IRawMessageOut<IRawCommandMessageOut> => {
	return getRawMessageType(message) === MessageType.COMMAND;
};
export const isRawEventMessage = (
	message: IRawMessageOut | IRawMessageIn,
): message is IRawMessageIn<IRawEventMessageIn> => {
	return getRawMessageType(message) === MessageType.EVENT;
};
